/**
 * Row validation for the Key / Data Type / Value tables.
 *
 * Lifted rule for rule from the antd form (RestApiForm's validateKeyValuePairs
 * and validateDataTypes) so a resource the old form accepts is accepted here and
 * the other way round — the two write the same records, and a row one of them
 * refuses to save should not be savable through the other.
 *
 * One module rather than two copies: the editor calls it to decide whether the
 * resource can be saved, and ParamsTable calls it to mark the offending box. If
 * a rule changes, both move together.
 */

/* Values are stored typed, so 0 and false are legitimate entries — test for
   null/undefined/blank, never for truthiness. */
const toText = (value) => (value === null || value === undefined ? "" : String(value));

const NO_ERROR = { field: null, message: "" };

/**
 * @returns {{field: "key"|"value"|null, message: string}}
 */
export function validateRow(row) {
  const pair = row || {};
  const hasKey = toText(pair.key).trim() !== "";
  const hasValue = toText(pair.value).trim() !== "";

  // A row nobody has touched is the trailing blank, not a mistake.
  if (!hasKey && !hasValue && !pair.dateConfig) return NO_ERROR;

  /* A date row's value comes from its config, so the config is what gets
     checked. Only Fixed Date needs anything picked — the other two work their
     date out when the report runs. */
  if (pair.dataType === "date") {
    if (hasKey && !pair.dateConfig) return { field: "value", message: "Date configuration is required" };
    if (!hasKey && pair.dateConfig) return { field: "key", message: "Key is required" };
    if (hasKey && pair.dateConfig) {
      if (pair.dateConfig.dateOption === "fixedDate" && !pair.dateConfig.fixedDate) {
        return { field: "value", message: "Date is required" };
      }
    }
    return NO_ERROR;
  }

  // Half a pair is never sent: both halves or neither.
  if (hasKey && !hasValue) return { field: "value", message: "Value is required" };
  if (!hasKey && hasValue) return { field: "key", message: "Key is required" };

  // Both filled in — the value has to match the type the row claims.
  const value = toText(pair.value).trim();

  if (pair.dataType === "number") {
    if (!/^-?\d+(\.\d+)?$/.test(value)) {
      return { field: "value", message: "Must be a valid number (integer or decimal)" };
    }
    return NO_ERROR;
  }

  if (pair.dataType === "boolean") {
    if (["true", "false", "1", "0"].indexOf(value.toLowerCase()) === -1) {
      return { field: "value", message: "Must be true/false or 1/0" };
    }
    return NO_ERROR;
  }

  if (pair.dataType === "string") {
    try {
      encodeURIComponent(value);
    } catch (e) {
      return { field: "value", message: "Must be valid UTF-8 text" };
    }
  }

  return NO_ERROR;
}

/**
 * Every bad row in one table, keyed by the index ParamsTable renders by.
 *
 * @returns {Object<number, {field: string, message: string}>}
 */
export function validateRows(rows) {
  const errors = {};
  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const result = validateRow(row);
    if (result.field) errors[index] = result;
  });
  return errors;
}
